import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_import-hub-80/artifacts/r714uk97_BotBSlogo%20RGB.png";

// Real mountain photo band — fades from cream page into the deep-blue footer
function MountainRidge() {
  return (
    <div
      aria-hidden
      className="relative w-full overflow-hidden"
      style={{ height: "clamp(220px, 26vw, 360px)", background: "var(--bg-primary)" }}
    >
      <img
        src="/images/hero-mountains.webp"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: "center 47%", transform: "scale(1.18)", transformOrigin: "20% 50%" }}
      />
      {/* subtle blue tint keeps the photo in the site palette */}
      <div className="absolute inset-0" style={{ background: "rgba(14,44,70,0.18)" }} />
      {/* cream fade at the top so the page dissolves into the photo */}
      <div
        className="absolute inset-x-0 top-0"
        style={{
          height: "42%",
          background: "linear-gradient(to bottom, var(--bg-primary) 0%, rgba(244,239,227,0) 100%)",
        }}
      />
      {/* deep-blue fade at the bottom so the photo melts into the footer */}
      <div
        className="absolute inset-x-0 bottom-0"
        style={{
          height: "52%",
          background:
            "linear-gradient(to bottom, rgba(14,44,70,0) 0%, rgba(14,44,70,0.45) 40%, rgba(14,44,70,0.92) 75%, #0E2C46 100%)",
        }}
      />
    </div>
  );
}

export default function SiteFooter({ settings }) {
  return (
    <footer data-testid="site-footer" className="relative mt-32">
      {/* Mountain ridge transition into the deep-blue footer */}
      <div style={{ background: "var(--bg-primary)" }}>
        <MountainRidge />
      </div>

      <div className="section-deep relative overflow-hidden">
        {/* faint mountain watermark for depth */}
        <div className="mountain-watermark" style={{ opacity: 0.08 }} />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-start gap-5">
              <img
                src={LOGO_URL}
                alt="Bulls of the Big Sky"
                className="block w-24 h-24 md:w-28 md:h-28 object-contain flex-shrink-0"
                style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.35))" }}
              />
              <div>
                <div className="font-display text-3xl md:text-4xl leading-tight" style={{ color: "var(--surface)" }}>
                  Bulls of the<br />Big Sky
                </div>
                <div
                  className="font-display text-[11px] mt-2"
                  style={{ color: "var(--sky-pale)", letterSpacing: "0.32em", fontWeight: 600 }}
                >
                  Simmental ★ Angus ★ SimAngus
                </div>
              </div>
            </div>
            <p className="mt-6 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.72)" }}>
              Eight programs. One catalog. Backed by repeat buyers across the country since 2009.
            </p>
            <div className="mt-6 font-script text-2xl" style={{ color: "var(--sky-pale)" }}>
              Built where the mountains meet the prairie.
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow mb-5" style={{ color: "var(--sky-pale)" }}>Sale Office</div>
            <div className="space-y-2 text-sm" style={{ color: "rgba(255,255,255,0.78)" }}>
              <a href="tel:208-267-2668" data-testid="footer-phone" className="block hover:text-white transition">
                {settings?.contact_phone || "208-267-2668"}
              </a>
              <a
                href={`mailto:${settings?.contact_email || "rymocattle@gmail.com"}`}
                data-testid="footer-email"
                className="block hover:text-white transition break-all"
              >
                {settings?.contact_email || "rymocattle@gmail.com"}
              </a>
              <div>427 Peaceful Way<br />Bonners Ferry, ID 83805</div>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow mb-5" style={{ color: "var(--sky-pale)" }}>Navigate</div>
            <ul className="space-y-2 text-sm" style={{ color: "rgba(255,255,255,0.78)" }}>
              <li><Link data-testid="footer-link-legacy" to="/sale/legacy" className="hover:text-white">Legacy Sale</Link></li>
              <li><Link data-testid="footer-link-annual" to="/sale/annual" className="hover:text-white">Annual Sale</Link></li>
              <li><Link data-testid="footer-link-consignors" to="/consignors" className="hover:text-white">Consignors</Link></li>
              <li><Link to="/notable-bulls" className="hover:text-white">The Bulls</Link></li>
              <li><Link to="/donor-cows" className="hover:text-white">The Females</Link></li>
              <li><Link to="/heterosis" className="hover:text-white">Heterosis</Link></li>
              <li><Link to="/accreditation" className="hover:text-white">Accreditation</Link></li>
              <li><Link data-testid="footer-link-buyer" to="/buyer-info" className="hover:text-white">Buyer Info &amp; Terms</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow mb-5" style={{ color: "var(--sky-pale)" }}>Partners</div>
            <ul className="space-y-2 text-sm" style={{ color: "rgba(255,255,255,0.78)" }}>
              <li><a href="https://www.simmental.org/" target="_blank" rel="noreferrer" className="hover:text-white">American Simmental Association</a></li>
              <li><a href="https://www.alliedgeneticresources.com/" target="_blank" rel="noreferrer" className="hover:text-white">Allied Genetic Resources</a></li>
              <li><a href="https://www.dvauction.com/events/254347-bulls-big-sky-annual-production-2-16-26/lots" target="_blank" rel="noreferrer" className="hover:text-white">DV Auction</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <div className="text-[11px] eyebrow-muted" style={{ color: "rgba(255,255,255,0.5)" }}>
              © {new Date().getFullYear()}  •  Bulls of the Big Sky  •  All Rights Reserved
            </div>
            <Link
              to="/admin/login"
              data-testid="footer-admin-link"
              className="text-[11px] eyebrow-muted hover:text-white transition"
              style={{ color: "rgba(255,255,255,0.5)" }}
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
